import { Box } from '@mui/material';
import { useStacklineTheme } from '@stackline/ui';
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { BrandLogo } from 'src/components/BeaconV2Components/Generic/BrandLogo';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import SplineChartV2, { SplineChartV2Series } from 'src/components/BeaconV2Components/SplineChartV2/SplineChartV2';
import { useComparisonWeekIds } from 'src/components/BeaconV2Components/SplineChartV2/useComparisonWeekIds';
import {
  getColorForMetricChangeValue,
  getIconForMetricChange
} from 'src/components/BeaconV2Components/utils/chartStyles';
import { BEACON_PRO_ENTITY_CARD_WIDTH } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import { useAppSelector, useMetricFormatter } from 'src/utils/Hooks';
import { getLastWeekIdOfYear, yearPartOfWeekId } from 'src/utils/dateUtils';
import { METRICTYPE } from 'src/utils/entityDefinitions';

interface SplineChartData {
  weekId: string;
  currentValue: number;
}

interface Brand {
  id: number;
  name: string;
  displayName: string;
  currentRetailSales: number;
  retailSalesChange: number;
  retailSalesChangePercentage: number;
  currentSplineChartData: SplineChartData[];
  currentSplineChartComparisonData: SplineChartData[];
}

interface BrandCardProps {
  val: Brand;
}

/**
 * Generic container Category Card
 */
const BrandCard: React.FC<BrandCardProps> = ({ val }) => {
  const theme = useStacklineTheme();
  const {
    displayName,
    id,
    currentRetailSales,
    retailSalesChange,
    retailSalesChangePercentage,
    currentSplineChartData,
    currentSplineChartComparisonData
  } = val;
  const { searchParams, additionalParams } = useAppSelector((state) => state.app.queryParams);
  const formatMetric = useMetricFormatter();

  // Get comparison week IDs to detect YTD + Prior Year mode
  const { isYTDWithPriorYear, comparisonStartWeekId, comparisonEndWeekId } = useComparisonWeekIds();

  // For YTD + Prior Year, expand categories to include full prior year range
  // This ensures the comparison series shows all of last year's data
  const categoryOverrides = useMemo(() => {
    if (isYTDWithPriorYear && comparisonStartWeekId && comparisonEndWeekId) {
      // Use the comparison period's start and the last week of that year as the category range
      const comparisonYear = yearPartOfWeekId(comparisonStartWeekId);
      return {
        categoryStartWeekId: comparisonStartWeekId,
        categoryEndWeekId: getLastWeekIdOfYear(comparisonYear)
      };
    }
    return {};
  }, [isYTDWithPriorYear, comparisonStartWeekId, comparisonEndWeekId]);

  const primarySeries: SplineChartV2Series = useMemo(
    () => ({
      id: 'retail-sales',
      name: 'Retail Sales',
      data: (currentSplineChartData || [])
        .map((data) => ({
          weekId: Number(data.weekId),
          value: data.currentValue
        }))
        .sort((a, b) => a.weekId - b.weekId)
    }),
    [currentSplineChartData]
  );

  const comparisonSeries: SplineChartV2Series | undefined = useMemo(() => {
    if (!currentSplineChartComparisonData || currentSplineChartComparisonData.length === 0) {
      return undefined;
    }
    return {
      id: 'retail-sales-comparison',
      name: 'Retail Sales (Comparison)',
      data: currentSplineChartComparisonData
        .map((data) => ({
          weekId: Number(data.weekId),
          value: data.currentValue
        }))
        .sort((a, b) => a.weekId - b.weekId)
    };
  }, [currentSplineChartComparisonData]);

  return (
    <div
      style={{
        width: BEACON_PRO_ENTITY_CARD_WIDTH,
        height: '484px',
        borderRadius: theme.spacing.sm,
        border: `1px solid ${theme.colors.primaryGray}`,
        position: 'relative'
      }}
    >
      <Flex
        flexDirection="column"
        sx={{
          paddingY: theme.spacing.md
        }}
      >
        <Flex
          sx={{
            paddingX: theme.spacing.md
          }}
        >
          <div
            style={{
              width: '40px',
              height: '40px'
            }}
          >
            <Link to={`/brand/${id}${searchParams}${additionalParams}`}>
              <BrandLogo
                width={40}
                height={40}
                brandId={id}
                displayName={val.displayName}
                elementCardVariant="medium"
              />
            </Link>
          </div>
        </Flex>
        <Flex padding={`${theme.spacing.md} ${theme.spacing.md} ${theme.spacing.md}`}>
          <Link to={`/brand/${id}${searchParams}${additionalParams}`}>
            <Text variant="subtitle1" sx={{ fontSize: '18px' }}>
              {displayName}
            </Text>
          </Link>
        </Flex>
        <Box style={{ marginTop: '40px' }}>
          <SplineChartV2
            preview={{ width: +BEACON_PRO_ENTITY_CARD_WIDTH.replace('px', ''), height: 115 }}
            primarySeries={primarySeries}
            comparisonSeries={comparisonSeries}
            metricType={METRICTYPE.MONEY}
            removeSpacing
            {...categoryOverrides}
            chartOptionsOverride={{
              chart: {
                marginLeft: -2
              },
              plotOptions: {
                series: {
                  lineWidth: 2
                }
              }
            }}
          />
        </Box>
      </Flex>
      <Flex
        flexDirection="column"
        sx={{
          paddingX: theme.spacing.md
        }}
      >
        <div style={{ position: 'absolute', bottom: theme.spacing.md }}>
          <Flex flexDirection="column" gap="xs" justifyContent="center">
            <Link to={`/brand/${id}${searchParams}&tab=sales&subtab=retailSales`}>
              <Text transform="uppercase" variant="subtitle2">
                Retail Sales
              </Text>
            </Link>
            <Flex justifyContent="center">
              <Text variant="body1">
                {formatMetric(currentRetailSales, METRICTYPE.MONEY, {
                  decimalPlaces: 1,
                  showFullValue: false
                })}
              </Text>
              <Flex
                alignItems="center"
                sx={{
                  paddingX: theme.spacing.xs
                }}
              >
                <div>{getIconForMetricChange(retailSalesChange)}</div>

                <Flex
                  sx={{
                    paddingX: theme.spacing.xs
                  }}
                >
                  <Text variant="subtitle2" color={getColorForMetricChangeValue(retailSalesChangePercentage)}>
                    {' '}
                    {formatMetric(Math.abs(retailSalesChange), METRICTYPE.MONEY, {
                      decimalPlaces: 1,
                      showFullValue: false
                    })}{' '}
                    (
                    {formatMetric(Math.abs(retailSalesChangePercentage), METRICTYPE.PERCENT, {
                      decimalPlaces: 1,
                      showFullValue: true,
                      showNegative: true
                    })}
                    )
                  </Text>
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        </div>
      </Flex>
    </div>
  );
};

export default BrandCard;
